/**
 * DevSimulatorConfirmModal
 *
 * Modale di conferma per attivazione/disattivazione del DevSimulator.
 * Sostituisce il vecchio alert() con una modale in stile con il programma.
 */

import React, { useEffect } from 'react';
import { Beaker, X, CheckCircle, XCircle } from 'lucide-react';
import { useDevSimulator } from './DevSimulatorContext';

export const DevSimulatorConfirmModal: React.FC = () => {
  const { confirmModal, closeConfirmModal } = useDevSimulator();

  // Chiudi con Escape
  useEffect(() => {
    if (!confirmModal.visible) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeConfirmModal();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [confirmModal.visible, closeConfirmModal]);

  // Auto-chiudi dopo 3 secondi
  useEffect(() => {
    if (!confirmModal.visible) return;
    const timer = setTimeout(closeConfirmModal, 3000);
    return () => clearTimeout(timer);
  }, [confirmModal.visible, closeConfirmModal]);

  if (!confirmModal.visible) return null;

  const isEnabling = confirmModal.isEnabling;

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={closeConfirmModal}
      />

      {/* Modal */}
      <div
        className={`
          relative w-80 max-w-[90vw] rounded-2xl shadow-2xl border overflow-hidden
          animate-in zoom-in-95 fade-in duration-200
          ${isEnabling
            ? 'bg-gradient-to-br from-slate-800 to-slate-900 border-amber-500/40'
            : 'bg-gradient-to-br from-slate-800 to-slate-900 border-slate-600'
          }
        `}
      >
        {/* Header con gradiente */}
        <div className={`px-5 py-4 flex items-center gap-3 ${
          isEnabling
            ? 'bg-gradient-to-r from-amber-600/30 to-orange-600/30'
            : 'bg-gradient-to-r from-slate-700/50 to-slate-600/50'
        }`}>
          <div className={`p-2 rounded-xl ${
            isEnabling ? 'bg-amber-500/20' : 'bg-slate-600/50'
          }`}>
            <Beaker className={`w-6 h-6 ${isEnabling ? 'text-amber-400' : 'text-slate-400'}`} />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-white text-sm">DEV SIMULATOR</h3>
            <p className={`text-xs ${isEnabling ? 'text-amber-300' : 'text-slate-400'}`}>
              {isEnabling ? 'Attivato' : 'Disattivato'}
            </p>
          </div>
          <button
            onClick={closeConfirmModal}
            className="p-1 rounded-lg hover:bg-white/10 transition-colors"
          >
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4">
          <div className={`flex items-start gap-3 p-3 rounded-xl ${
            isEnabling ? 'bg-amber-500/10' : 'bg-slate-700/30'
          }`}>
            {isEnabling ? (
              <CheckCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            ) : (
              <XCircle className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
            )}
            <p className="text-sm text-slate-300 leading-relaxed">
              {confirmModal.message}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 pb-4">
          <button
            onClick={closeConfirmModal}
            className={`
              w-full py-2.5 rounded-xl text-sm font-semibold transition-all
              ${isEnabling
                ? 'bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 border border-amber-500/30'
                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-600/50 border border-slate-600'
              }
            `}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

export default DevSimulatorConfirmModal;
